import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import type {
  FacebookLoginData,
  SignUpEmailDataHelpers,
  SignUpGoogleResponseData,
} from '@packages/shared/core/auth/entities';
import { PagePath } from '@packages/shared/core/common/constants/routes';
import { joinUrls } from '@packages/shared/core/common/utils/url';
import { LoadingStatuses } from '@packages/shared/core/common/entities';
import { ApiError } from '@packages/shared/core/common/errors';
import { useBackRedirect, useLogger, useRouter, useServices } from '@packages/shared/core/common/hooks';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import type { BlurredSignUpStepData, QuizScreenProps } from '@packages/shared/core/quiz/entities';
import { getSignUpError, getUserPreferences } from '@packages/shared/core/user/store/selectors';

const routingCondition = `popHistoryModal`;
type BlurredSignUpStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const BlurredSignUpStep = ({ id, renderQuizScreen }: BlurredSignUpStepProps) => {
  const logger = useLogger();

  const router = useRouter();

  const { analyticsService, authService } = useServices();

  const {
    back,
    next,
    stepData,
    signUpWithGoogle,
    totalSteps,
    stepOrder,
    baseUrl,
    userPreferences,
    updateUserPreferences,
    signUpWithFacebook,
  } = useQuizStep(id);

  const flowVariant = userPreferences.srVariant as 'cheating' | undefined;

  const baseRoute =
    flowVariant === 'cheating' ? PagePath.SIMPLE_REGISTRATION_CHEATING_QUIZ : PagePath.SIMPLE_REGISTRATION_QUIZ;

  useBackRedirect(() => {
    if (stepData.prev === routingCondition) {
      router.push(joinUrls(baseRoute, routingCondition));
    }
  });

  const { signUpByGoogleStatus, signUpByFacebookStatus } = useSelector(getUserPreferences);
  const errorMessage = useSelector(getSignUpError);

  useEffect(() => {
    if (userPreferences.signUpByFacebookStatus === LoadingStatuses.FULFILLED) {
      router.push(joinUrls(baseUrl, 'phoneNumber'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signUpByFacebookStatus]);

  if (!stepData) {
    // TODO add fallback
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const { otherEvent } = stepData as BlurredSignUpStepData;

  const goal = (stepData as BlurredSignUpStepData).getGoal(userPreferences.goal);

  const goBack = async () => back();

  const handleSubmit = async (data: { email: string }, { setErrors }: SignUpEmailDataHelpers) => {
    try {
      analyticsService.registrationFormClicked('common');

      const isValidEmailPromise = authService.isValidEmail(data.email);
      const doesEmailExistsPromise = authService.doesEmailExists(data.email);

      const [doesEmailExist] = await Promise.all([doesEmailExistsPromise, isValidEmailPromise]);

      updateUserPreferences('email', data.email);
      updateUserPreferences('emailAlreadyExists', doesEmailExist.exists);

      await next();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors({ email: err.originalError.message });
      } else {
        setErrors({ email: 'Something went wrong. Try again later' });
      }
    }
  };

  const doSignUpWithGoogle = async (data: SignUpGoogleResponseData) => {
    analyticsService.registrationFormClicked('google');
    await signUpWithGoogle(data);

    return router.push(joinUrls(baseUrl, 'phoneNumber'));
  };

  const doSignUpWithFacebook = async (data: FacebookLoginData) => {
    analyticsService.registrationFormClicked('fb');
    await signUpWithFacebook(data);
  };

  const openModal = () => {
    if (otherEvent) {
      analyticsService.trackQuizEvent(otherEvent.openModal, {});
    }
  };

  const closeModal = () => {
    if (otherEvent) {
      analyticsService.trackQuizEvent(otherEvent.closeModal, {});
    }
  };

  const clickExplanation = () => {
    if (otherEvent) {
      analyticsService.trackQuizEvent(otherEvent.clickExplanation, {});
    }
  };

  return (
    <>
      {renderQuizScreen({
        onBack: goBack,
        goNext: handleSubmit,
        errorMessage,
        signUpWithGoogle: doSignUpWithGoogle,
        signUpWithFacebook: doSignUpWithFacebook,
        signUpByFacebookStatus,
        signUpByGoogleStatus,
        totalSteps,
        goal,
        stepOrder,
        onOpenModal: openModal,
        onCloseModal: closeModal,
        onClickExplanation: clickExplanation,
        ...(stepData as BlurredSignUpStepData),
      })}
    </>
  );
};

export default BlurredSignUpStep;
